import type { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import React from 'react';

/**
 * Column shape consumed by `FilterableDataTable` (react-table v7-style field names).
 * Converted to TanStack `ColumnDef` in `tableColumnsToColumnDefs`.
 */
export type FilterableDataTableColumnDefinition<D extends object> = {
  id?: string;
  Header?: React.ReactNode | React.ComponentType<unknown>;
  accessor?: string | ((row: D, index: number) => unknown);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Cell?: React.ComponentType<any> | ((props: any) => React.ReactNode);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Filter?: React.ComponentType<any> | ((props: any) => React.ReactNode);
  disableSortBy?: boolean;
  disableFilters?: boolean;
  width?: number | string;
  minWidth?: number;
  maxWidth?: number;
  [key: string]: unknown;
};

function renderHeaderContent<D extends object>(
  def: FilterableDataTableColumnDefinition<D>,
  ctx: HeaderContext<D, unknown>,
) {
  const { Header } = def;
  if (Header == null) {
    return null;
  }
  if (React.isValidElement(Header)) {
    return Header;
  }
  if (typeof Header === 'function') {
    const Cmp = Header as React.ComponentType<unknown>;
    return <Cmp {...(ctx as unknown as Record<string, unknown>)} />;
  }
  return String(Header);
}

function cellRenderProps<D extends object>(ctx: CellContext<D, unknown>) {
  const { cell, column, row, getValue } = ctx;
  return { cell, column, row, value: getValue() };
}

function renderCellContent<D extends object>(
  def: FilterableDataTableColumnDefinition<D>,
  ctx: CellContext<D, unknown>,
) {
  const { Cell } = def;
  if (!Cell) {
    const v = ctx.getValue();
    return v == null ? '' : String(v);
  }
  const props = cellRenderProps(ctx);
  if (typeof Cell === 'function') {
    const Cmp = Cell as React.ComponentType<typeof props>;
    return <Cmp {...props} />;
  }
  const Cmp = Cell as React.ComponentType<typeof props>;
  return <Cmp {...props} />;
}

export function tableColumnsToColumnDefs<D extends object>(
  columns: FilterableDataTableColumnDefinition<D>[],
  defaultColumn?: Partial<FilterableDataTableColumnDefinition<D>>,
): ColumnDef<D, unknown>[] {
  return columns.map((def) => {
    const Filter = def.Filter ?? defaultColumn?.Filter;
    const enableSorting = def.disableSortBy !== true;
    const enableColumnFilter = def.disableFilters !== true;

    const meta = {
      columnWidth: def.width,
      filterComponent: Filter,
      tableColumn: def as Record<string, unknown>,
    };

    const filterFn: ColumnDef<D, unknown>['filterFn'] = enableColumnFilter ? 'auto' : undefined;

    const base = {
      id: def.id,
      meta,
      enableSorting,
      enableColumnFilter,
      header: (ctx: HeaderContext<D, unknown>) => renderHeaderContent(def, ctx),
      cell: (ctx: CellContext<D, unknown>) => renderCellContent(def, ctx),
      filterFn,
    };

    if (typeof def.accessor === 'function') {
      return { ...base, accessorFn: def.accessor } as ColumnDef<D, unknown>;
    }

    if (def.accessor != null && typeof def.accessor === 'string') {
      return { ...base, accessorKey: def.accessor } as ColumnDef<D, unknown>;
    }

    return { ...base, id: def.id ?? 'column' } as ColumnDef<D, unknown>;
  });
}
